import { Server } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { TopEdgeEntry } from '../../services/dashboardSummary.service';
import { formatBytes } from '../../utils/analytics';

interface TopEdgesCardProps {
    data: TopEdgeEntry[];
    loading: boolean;
}

const edgeColors = [
    'bg-blue-500',
    'bg-purple-500',
    'bg-cyan-500',
    'bg-emerald-500',
    'bg-orange-500',
];

// VSNP-966: ranks the busiest edges by total WAN/LAN throughput (replaces the
// old fleet-wide "Top Ports" card, which ranked L4 destination ports and told
// an operator nothing about *which edge* traffic was on). Each row drills into
// that edge's detail page.
export const TopEdgesCard = ({ data, loading }: TopEdgesCardProps) => {
    if (loading) {
        return (
            <div className="card animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="flex items-center space-x-3">
                            <div className="h-4 bg-gray-200 rounded w-24"></div>
                            <div className="flex-1 h-4 bg-gray-200 rounded"></div>
                            <div className="h-4 bg-gray-200 rounded w-12"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Top Edges</h2>
                    <Link
                        to="/analytics/traffic"
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                        View All →
                    </Link>
                </div>
                <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Server className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">No edge traffic data available</p>
                    <p className="text-xs text-gray-400 mt-1">Traffic will appear when edges are online</p>
                </div>
            </div>
        );
    }

    // Bar width scales by bytes relative to the busiest shown edge, so the ranking
    // stays visible regardless of fleet size — on a large fleet every edge's share of
    // the fleet total is tiny, which would flatten percentage-scaled bars. (VSNP-966)
    const maxBytes = Math.max(...data.map(e => e.bytes), 1);
    // Adaptive precision: sub-10% shares show a decimal so busy edges on a big fleet
    // don't all read "0%".
    const formatPercent = (p: number): string =>
        p >= 10 ? `${Math.round(p)}%` : p >= 0.1 ? `${p.toFixed(1)}%` : '<0.1%';

    return (
        <div className="card">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Top Edges</h2>
                <Link
                    to="/analytics/traffic"
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                    View All →
                </Link>
            </div>

            <div className="space-y-3">
                {data.slice(0, 5).map((entry, index) => {
                    const barWidth = (entry.bytes / maxBytes) * 100;
                    const colorClass = edgeColors[index % edgeColors.length];
                    const edgeName = entry.edgeName ?? entry.edgeId;

                    return (
                        <div key={entry.edgeId} className="group">
                            <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center space-x-2 min-w-0">
                                    <Link
                                        to={`/edges/${entry.edgeId}`}
                                        className="text-sm font-medium text-gray-900 truncate hover:text-primary-600"
                                        title={edgeName}
                                    >
                                        {edgeName}
                                    </Link>
                                </div>
                                <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                                    <span className="text-xs text-gray-500">{formatBytes(entry.bytes)}</span>
                                    <span className="text-xs font-medium text-gray-700 w-12 text-right">
                                        {formatPercent(entry.percentage)}
                                    </span>
                                </div>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                    className={`h-2 rounded-full transition-all duration-300 ${colorClass}`}
                                    style={{ width: `${barWidth}%` }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">
                        Top {Math.min(5, data.length)} busiest edges (last 1h)
                    </span>
                    <span className="font-medium text-gray-700">
                        {formatBytes(data.reduce((sum, e) => sum + e.bytes, 0))} total
                    </span>
                </div>
            </div>
        </div>
    );
};

export default TopEdgesCard;
